import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AppRoutes from './routes';
import AuthProvider from './redux/authentication/utils/core/authProvider';
import UserSwitchGuard from './userSwitchGuard';
import EnhancedErrorBoundary from './utils/ErrorHandling/EnhancedErrorBoundary';
import { fetchUserLanguage } from './redux/settingsPages/language/languageActions';
import { fetchAccessibilitySettings } from './redux/settingsPages/accessibility/accessibilityActions';
import { fetchFeatureFlagsThunk } from './redux/featureFlags/featureFlagsActionThunks';
import { ThemeProvider } from './theme/ThemeContext';
import './App.css';

function App() {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const rawUserId = useSelector((s) => s.auth?.user?.id ?? null);
  const isAuthenticated = useSelector((s) => s.auth?.isAuthenticated);

  // `remountKey` used to just be `rawUserId` (falling back to a synthetic
  // 'anon' placeholder), which forced a full remount of everything below
  // -- including every mount-effect data fetch in the whole app -- the
  // moment auth resolved from "not yet known" to a real user on every
  // single normal page load, not just on an actual account switch.
  // Confirmed live via a Playwright network trace: this alone doubled
  // requests like `/me/stocks` on top of React 18 StrictMode's own
  // dev-only double-invoke, independent of it. Only bump the key on a
  // genuine real-user -> different-real-user transition (an account
  // switch without a full page reload); UserSwitchGuard below already
  // handles purging persisted Redux state for that same transition, so
  // this key is only about resetting in-memory component state.
  const [remountKey, setRemountKey] = useState(0);
  const [trackedUserId, setTrackedUserId] = useState(rawUserId);
  if (rawUserId !== trackedUserId) {
    if (trackedUserId !== null && rawUserId !== null) {
      setRemountKey((k) => k + 1);
    }
    setTrackedUserId(rawUserId);
  }
  const currentLanguage = useSelector((s) => s.language?.current);
  const accessibility = useSelector((s) => s.accessibility);

  // Feature flags gate public nav/routing too, so fetch unconditionally on load rather
  // than waiting on auth -- the initialState default (matching the old hardcoded object)
  // covers rendering before this resolves.
  useEffect(() => {
    dispatch(fetchFeatureFlagsThunk());
  }, [dispatch]);

  // Load the user's saved language preference once they're authenticated.
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchUserLanguage());
    }
  }, [isAuthenticated, dispatch]);

  // Load the user's saved accessibility preferences once they're
  // authenticated (Settings > Accessibility, EAA) -- same pattern as
  // language above.
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchAccessibilitySettings());
    }
  }, [isAuthenticated, dispatch]);

  // Apply accessibility preferences app-wide via attributes/classes on
  // <html>, the same mechanism ThemeContext.jsx uses for the `dark` class
  // -- real, global effects, not a settings-page-only preview. See
  // index.css's "ACCESSIBILITY SETTINGS" section for the CSS these drive.
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.textScale = accessibility?.text_scale || 'normal';
    root.classList.toggle('reduce-motion-forced', !!accessibility?.reduced_motion);
    root.classList.toggle('high-contrast', !!accessibility?.high_contrast);
  }, [accessibility?.text_scale, accessibility?.reduced_motion, accessibility?.high_contrast]);

  // Keep i18next's active language in sync with the persisted preference,
  // whether it just loaded from the backend or was changed on the language
  // settings page.
  useEffect(() => {
    if (currentLanguage && currentLanguage !== i18n.language) {
      i18n.changeLanguage(currentLanguage);
    }
  }, [currentLanguage, i18n]);


  // Add debug info for error boundary
  const debugInfo = {
    userId: rawUserId,
    component: 'App',
    timestamp: new Date().toISOString()
  };

  return (
    <ThemeProvider>
    <EnhancedErrorBoundary debugInfo={debugInfo}>
      {/* Remove BrowserRouter - it's already in AppContainer */}
      <AuthProvider>
        <UserSwitchGuard />
        <div key={remountKey}>
          <EnhancedErrorBoundary 
            debugInfo={{ ...debugInfo, context: 'AppRoutes' }}
            fallback={({ error, retry }) => (
              <div className="w-full h-full flex items-center justify-center bg-gray-50 px-4">
                <div className="text-center max-w-md">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Navigation Error
                  </h2>
                  <p className="text-gray-600 mb-4">
                    There was a problem loading the page content.
                  </p>
                  
                  {/* Enhanced error display for debugging */}
                  {process.env.NODE_ENV === 'development' && error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-left">
                      <p className="text-sm font-medium text-red-800 mb-1">
                        Debug Info:
                      </p>
                      <p className="text-xs text-red-700 font-mono">
                        {error.message}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Component: AppRoutes | User: {rawUserId ?? 'anon'}
                      </p>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <button
                      onClick={retry}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
                    >
                      Try Again
                    </button>
                    
                    {process.env.NODE_ENV === 'development' && (
                      <button
                        onClick={() => {
                          //console.log('Current error debugger state:');
                          //console.log(window.reactDebugger?.generateReport());
                        }}
                        className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm"
                      >
                        Debug Report
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          >
            <AppRoutes />
          </EnhancedErrorBoundary>
        </div>
      </AuthProvider>
    </EnhancedErrorBoundary>
    </ThemeProvider>
  );
}

export default App;